import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Eye, Upload, Sparkles, Trophy, ArrowRight, Check, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/navigation/Header';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  const features = [
  {
    icon: <Zap size={22} className="text-white" />,
    iconBg: 'from-violet-500 to-purple-600',
    title: 'Instant Rankings',
    desc: 'Upload your photos and get AI-powered rankings in seconds. See which photos perform best.'
  },
  {
    icon: <Eye size={22} className="text-white" />,
    iconBg: 'from-pink-500 to-rose-500',
    title: 'Discover Your Angles',
    desc: 'Learn which poses, lighting, and expressions work best for you with detailed insights.'
  },
  {
    icon: <Heart size={22} className="text-white" />,
    iconBg: 'from-emerald-500 to-teal-500',
    title: 'Find Your Most Attractive Photos',
    desc: 'Become popular on dating sites and social media using only your best photos!'
  }];


  const steps = [
  {
    num: '1',
    icon: <Upload size={24} className="text-violet-400" />,
    title: 'Upload Photos',
    desc: 'Select and upload the photos you want to analyze from your device.'
  },
  {
    num: '2',
    icon: <Sparkles size={24} className="text-violet-400" />,
    title: 'AI Analysis',
    desc: 'Our AI instantly processes and evaluates each photo using advanced algorithms.'
  },
  {
    num: '3',
    icon: <Trophy size={24} className="text-violet-400" />,
    title: 'View Rankings',
    desc: 'Get detailed rankings and insights about which photos work best for you.'
  }];


  const plans = [
  {
    name: 'Starter Pack',
    price: '$9.99',
    perCredit: '$1.00 per credit',
    credits: '10 Credits',
    creditsColor: 'from-violet-500 to-purple-600',
    badge: null,
    savings: null,
    features: [
    '10 photo analysis credits',
    'Valid for 6 months',
    'All ranking features',
    'Email support'],

    popular: false
  },
  {
    name: 'Popular Pack',
    price: '$19.99',
    perCredit: '$0.80 per credit',
    credits: '25 Credits',
    creditsColor: 'from-violet-500 to-cyan-500',
    badge: 'Most Popular',
    savings: '20% savings',
    features: [
    '25 photo analysis credits',
    'Valid for 12 months',
    'All ranking features',
    'Priority email support',
    'Advanced comparison tools'],

    popular: true
  },
  {
    name: 'Pro Pack',
    price: '$34.99',
    perCredit: '$0.70 per credit',
    credits: '50 Credits',
    creditsColor: 'from-cyan-500 to-blue-500',
    badge: null,
    savings: '30% savings',
    features: [
    '50 photo analysis credits',
    'Valid for 12 months',
    'All ranking features',
    'Priority support',
    'Advanced comparison tools',
    'Export & sharing features'],

    popular: false
  }];


  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.12 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } }
  };

  return (
    <div className="min-h-screen bg-white font-body">
      <Helmet>
        <title>Angle! – AI-Powered Photo Ranking App for Dating & Social Media</title>
        <meta name="description" content="Upload your photos and get instant AI-powered rankings. Discover your best angles, improve your dating profile, and stand out on social media with Angle!" />
        <link rel="canonical" href="https://angle.dating/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://angle.dating/" />
        <meta property="og:title" content="Angle! – AI-Powered Photo Ranking App" />
        <meta property="og:description" content="Upload your photos and get instant AI-powered rankings. Discover your best angles and improve your dating profile." />
        <meta property="og:image" content="https://angle.dating/assets/images/3-top-images-1772836145108.png" />
        <meta property="og:site_name" content="Angle!" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Angle! – AI-Powered Photo Ranking App" />
        <meta name="twitter:description" content="Upload your photos and get instant AI-powered rankings. Discover your best angles and improve your dating profile." />
        <meta name="twitter:image" content="https://angle.dating/assets/images/3-top-images-1772836145108.png" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "Angle!",
          "url": "https://angle.dating",
          "description": "AI-powered photo ranking app that analyzes and ranks your photos to help you choose the best ones for dating profiles and social media.",
          "applicationCategory": "UtilitiesApplication",
          "operatingSystem": "Web",
          "offers": {
            "@type": "Offer",
            "price": "9.99",
            "priceCurrency": "USD"
          },
          "publisher": {
            "@type": "Organization",
            "name": "Angle App",
            "url": "https://angle.dating"
          }
        })}</script>
      </Helmet>
      {/* Shared site header */}
      <Header
        userProp={user}
        onNavigate={navigate}
        onPurchaseCredits={() => navigate('/purchase-credits')}
        onLogout={logout}
      />
      {/* HERO */}
      <section className="relative pt-20 pb-24 px-6 overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-violet-50/40 to-cyan-50/30 pointer-events-none" />
        <div className="absolute top-20 left-1/4 w-72 h-72 bg-violet-200/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-32 right-1/4 w-56 h-56 bg-cyan-200/30 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-violet-200 shadow-sm mb-8">

            <Sparkles size={14} className="text-violet-500" />
            <span className="text-xs font-semibold text-violet-700 tracking-wide">AI-Powered Photo Analysis</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-6"
            style={{ fontFamily: 'Crimson Text, serif' }}>

            Discover Your Best{' '}
            <span className="bg-gradient-to-r from-violet-600 to-cyan-500 bg-clip-text text-transparent">
              Photos with AI
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-500 mb-10 max-w-xl mx-auto leading-relaxed">

            Get instant AI-powered rankings of your photos. Find out which pictures showcase your best angles and boost your dating profile success.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">

            <button
              onClick={() => navigate(!loading && user ? '/user-dashboard' : '/auth')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-base shadow-lg shadow-violet-200 hover:opacity-90 hover:scale-[1.02] transition-all duration-200">

              <Sparkles size={18} />
              {!loading && user ? 'Go to Dashboard' : 'Get Started Free'}
            </button>
            <button
              onClick={() => scrollToSection('how-it-works')}
              className="flex items-center gap-2 px-7 py-3.5 rounded-xl border border-gray-200 bg-white text-gray-700 font-semibold text-base hover:border-gray-300 hover:bg-gray-50 transition-all duration-200">

              See How It Works
              <ArrowRight size={16} />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.45 }}
            className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500">

            {[
            { icon: '✅', text: 'No Sign-Up Required' },
            { icon: '⚡', text: 'Instant Results' },
            { icon: '🔒', text: '100% Private' }]?.
            map((item) =>
            <span key={item?.text} className="flex items-center gap-1.5"></span>
            )}
          </motion.div>
        </div>
      </section>
      {/* FEATURES */}
      <section id="features" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-center mb-16">

            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>
              Everything You Need to Shine
            </h2>
            <p className="text-gray-500 text-lg">AI-powered analysis that reveals your photo's true potential</p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6">

            {features?.map((f) =>
            <motion.div
              key={f?.title}
              variants={itemVariants}
              className="bg-white rounded-2xl border border-gray-100 p-7 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300">

                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${f?.iconBg} flex items-center justify-center mb-5 shadow-sm`}>
                  {f?.icon}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>{f?.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f?.desc}</p>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-[76px] px-6 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-center mb-16">

            <h2 className="text-4xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Crimson Text, serif' }}>Get results in 3 simple steps and become popular!

            </h2>
          </motion.div>

          <div className="relative">
            {/* Connector line */}
            <div className="hidden md:block absolute top-10 left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-0.5 bg-gradient-to-r from-violet-300 via-purple-300 to-cyan-300" />

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-3 gap-10">

              {steps?.map((step, i) =>
              <motion.div key={step?.title} variants={itemVariants} className="flex flex-col items-center text-center">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-200">
                      <span className="text-2xl font-bold text-white" style={{ fontFamily: 'Crimson Text, serif' }}>{step?.num}</span>
                    </div>
                  </div>
                  <div className="mb-3">{step?.icon}</div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" style={{ fontFamily: 'Crimson Text, serif' }}>{step?.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed max-w-[200px]">{step?.desc}</p>
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* 3-top-images illustration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="mt-14 flex justify-center">
            <img
              src="/assets/images/3-top-images-1772836145108.png"
              alt="3 top images illustration showing photo ranking results"
              className="w-full max-w-xl rounded-2xl" />

          </motion.div>
        </div>
      </section>
      {/* PRICING */}
      <section id="pricing" className="py-24 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-center mb-4">

            <div className="flex items-center justify-center gap-2 mb-4">
              <span className="text-xl">✨</span>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-violet-600 to-cyan-500 bg-clip-text text-transparent" style={{ fontFamily: 'Crimson Text, serif' }}>
                Purchase Photo Analysis Credits
              </h2>
              <span className="text-xl">⭐</span>
            </div>
            <p className="text-gray-500">Choose a credit package that fits your needs. Credits never expire and can be used anytime.</p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-3 gap-6 mt-12">

            {plans?.map((plan) =>
            <motion.div
              key={plan?.name}
              variants={itemVariants}
              className={`relative rounded-2xl p-7 flex flex-col ${
              plan?.popular ?
              'border-2 border-violet-400 shadow-xl shadow-violet-100 bg-white' :
              'border border-gray-200 bg-white shadow-sm'}`
              }>

                {plan?.badge &&
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full text-xs font-bold text-white bg-gradient-to-r from-violet-600 to-cyan-500 shadow-sm">
                      {plan?.badge}
                    </span>
                  </div>
              }

                <div className="mb-5">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className={`text-lg font-bold ${
                  plan?.popular ? 'bg-gradient-to-r from-violet-600 to-cyan-500 bg-clip-text text-transparent' : 'text-gray-800'}`
                  } style={{ fontFamily: 'Crimson Text, serif' }}>
                      {plan?.name}
                    </h3>
                    {plan?.savings &&
                  <span className="text-xs font-semibold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">{plan?.savings}</span>
                  }
                  </div>
                  <div className="text-4xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Crimson Text, serif' }}>{plan?.price}</div>
                  <div className="text-xs text-gray-400">{plan?.perCredit}</div>
                </div>

                <div className={`py-2.5 px-4 rounded-xl bg-gradient-to-r ${plan?.creditsColor} text-white text-sm font-semibold text-center mb-6 flex items-center justify-center gap-2`}>
                  <Sparkles size={14} />
                  {plan?.credits}
                </div>

                <ul className="flex flex-col gap-2.5 mb-8 flex-1">
                  {plan?.features?.map((feat) =>
                <li key={feat} className="flex items-start gap-2 text-sm text-gray-600">
                      <Check size={15} className="text-violet-500 mt-0.5 shrink-0" />
                      {feat}
                    </li>
                )}
                </ul>

                <button
                onClick={() => navigate('/purchase-credits')}
                className={`w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                plan?.popular ?
                'bg-gradient-to-r from-violet-600 to-cyan-500 text-white hover:opacity-90 shadow-md shadow-violet-200' :
                'border border-gray-200 text-gray-700 hover:border-violet-300 hover:text-violet-700 hover:bg-violet-50'}`
                }>

                  Select Package
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </section>
      {/* CTA */}
      <section className="py-24 px-6 bg-gradient-to-br from-violet-700 via-purple-700 to-cyan-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(255,255,255,0.15) 0%, transparent 60%), radial-gradient(circle at 70% 50%, rgba(255,255,255,0.1) 0%, transparent 60%)' }} />
        <div className="relative max-w-2xl mx-auto text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: 'Crimson Text, serif' }}>

            Ready to Find Your Best Photos?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="text-white/80 text-lg mb-10">

            Join thousands discovering their perfect angles with AI
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.2 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4">

            <button
              onClick={() => navigate(!loading && user ? '/user-dashboard' : '/auth')}
              className="px-8 py-3.5 rounded-xl bg-white text-violet-700 font-bold text-base hover:bg-violet-50 transition-all shadow-lg">

              {!loading && user ? 'Continue to Dashboard' : 'Get Started Free'}
            </button>
          </motion.div>
          <p className="text-white/50 text-sm mt-5">See the ranking results in seconds * Set your image strategy based on evidence
          </p>
        </div>
      </section>
      {/* FOOTER */}
      <footer className="bg-gray-950 text-gray-400 py-14 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-600 to-cyan-500 flex items-center justify-center">
                  <Sparkles size={15} className="text-white" />
                </div>
                <span className="text-white font-bold text-lg" style={{ fontFamily: 'Crimson Text, serif' }}>Angle!</span>
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">AI-Powered Photo Rankings</p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Product</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-gray-200 transition-colors">Features</button></li>
                <li><button onClick={() => scrollToSection('pricing')} className="hover:text-gray-200 transition-colors">Pricing</button></li>
                <li><button onClick={() => scrollToSection('how-it-works')} className="hover:text-gray-200 transition-colors">How It Works</button></li>
                <li>
                  <Link to="/frequently-asked-questions" className="hover:text-gray-200 transition-colors">
                    FAQ
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Company</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                <li><span className="hover:text-gray-200 transition-colors cursor-pointer">About</span></li>
                <li>
                  <Link to="/privacy-policy" className="hover:text-gray-200 transition-colors">Privacy</Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="hover:text-gray-200 transition-colors">Terms</Link>
                </li>
              </ul>
            </div>

            {/* Account */}
            <div>
              <h4 className="text-white font-semibold text-sm mb-4">Account</h4>
              <ul className="flex flex-col gap-2.5 text-sm">
                {!loading && user ? (
                  <li><button onClick={() => navigate('/user-dashboard')} className="hover:text-gray-200 transition-colors">Dashboard</button></li>
                ) : (
                  <>
                    <li><button onClick={() => navigate('/auth')} className="hover:text-gray-200 transition-colors">Sign In</button></li>
                    <li><button onClick={() => navigate('/auth')} className="hover:text-gray-200 transition-colors">Sign Up Free</button></li>
                  </>
                )}
                <li><button onClick={() => navigate('/purchase-credits')} className="hover:text-gray-200 transition-colors">Buy Credits</button></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-600">© {new Date()?.getFullYear()} Angle! All rights reserved.</p>
            <p className="text-xs text-gray-600">Made with ✨ AI</p>
          </div>
        </div>
      </footer>
    </div>
  );

};

export default LandingPage;